import clsx from 'clsx'
import { Fragment } from 'react/jsx-runtime'

import { FvLinkButton, Icon } from '@fv/client-components'
import type { ListShipment } from '@fv/client-types'
import { toggleArrayItem } from '@fv/models/core'

import { DividedContent } from '@/components/DividedContent'
import CheckboxField from '@/components/inputs/CheckboxField'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import { routes } from '@/routes'

import { HasQuote } from '../auth/HasPermission'
import { HideForVendor } from '../auth/HasRole'
import { useHasAskAboutShipment } from '../chat/api'
import {
  buildShipmentContextMessage,
  PjPromptPopup,
} from '../chat/PjPromptPopup'
import { useShipmentLabels } from '../labels/hooks'
import type { SliderName } from '../shipment-details-panel/types'
import { columnsRegistry, Td } from './Cells'
import { useColumnPreferences } from './hooks'
import type { TableProps } from './types'

type RowProps = TableProps & {
  load: ListShipment
  isActive?: boolean
  setDetailTab: (tab: SliderName) => void
}
export const ShipmentRow = ({
  setDetailTab,
  load,
  isActive,
  onActivate,
  setSelected,
  selected,
}: RowProps) => {
  const labels = useShipmentLabels(load)
  const labelLink =
    load.workflow === 'parcel' && labels.href ? labels.href : undefined
  const columnPrefs = useColumnPreferences()
  const visibleColumns = columnPrefs.filter(c => c.visible)
  const totalColumns = visibleColumns.length + 3 // +3 for checkbox, BOL, actions
  const hasAsk = useHasAskAboutShipment()

  return (
    <Fragment key={load.loadId}>
      <tr
        className={clsx(
          'border-fv-gray-300 border-dashed hover:bg-fv-blue-50 border-b hover:cursor-pointer bg-white group',
          {
            '!bg-fv-beer-light': isActive,
            ' [&_td]:!border-fv-beer-medium [&_td]:!border-b-0': isActive,
          },
        )}
        data-loadid={load.loadId} // for scroll to on list type toggle
        onClick={e =>
          !(e.target as HTMLElement).closest('a,input,label') &&
          onActivate?.(load)
        }
      >
        <Td
          className={clsx(
            'sticky left-0',
            isActive ? 'bg-fv-beer-light' : 'bg-white',
            !isActive && 'group-hover:!bg-fv-blue-50',
          )}
        >
          <HideForVendor>
            <CheckboxField
              name={load.loadId}
              className="px-1"
              checked={selected.includes(load.loadId)}
              onChange={e => {
                setSelected(p =>
                  toggleArrayItem(p, load.loadId, e.target.checked),
                )
              }}
            />
          </HideForVendor>
        </Td>
        <Td
          className={clsx(
            'sticky left-[calc(4rem)]',
            isActive ? 'bg-fv-beer-light' : 'bg-white',
            !isActive && 'group-hover:!bg-fv-blue-50',
          )}
        >
          {load.bol?.bolNumber}
        </Td>
        {visibleColumns.map(col => {
          const def = columnsRegistry[col.id]
          return def ? <def.Cell key={col.id} load={load} /> : null
        })}
        <Td
          className={clsx(
            '!text-center sticky right-0',
            isActive ? '!bg-fv-beer-light' : 'bg-white',
            !isActive && 'group-hover:!bg-fv-blue-50',
          )}
        >
          <ShipperTooltip label="More details...">
            <FvLinkButton to={routes.details(load.loadId)} icon="ellipsis-h" />
          </ShipperTooltip>
        </Td>
      </tr>
      {isActive && (
        <tr className={clsx('bg-fv-beer-xlight')}>
          <td colSpan={totalColumns} className="!border-fv-beer-medium">
            <div className="sticky left-4 inline-block fa-width-auto">
              <DividedContent theme="beer">
                <Icon
                  icon="arrow-turn-down-right"
                  className="text-fv-beer-dark ml-1"
                />
                <FvLinkButton
                  onClick={() => setDetailTab('documents')}
                  icon="file"
                >
                  Documents
                </FvLinkButton>
                <HideForVendor>
                  <FvLinkButton
                    onClick={() => setDetailTab('track')}
                    icon="map-marker"
                  >
                    Tracking
                  </FvLinkButton>
                  <HasQuote>
                    <FvLinkButton
                      onClick={() => setDetailTab('messages')}
                      icon="comment-alt-lines"
                    >
                      Messages
                    </FvLinkButton>
                  </HasQuote>
                </HideForVendor>
                {labels.hasLabels && (
                  <FvLinkButton
                    to={labelLink}
                    target={labelLink ? '_blank' : undefined}
                    onClick={() => {
                      if (!labelLink) {
                        setDetailTab('labels')
                      }
                    }}
                    icon="tags"
                  >
                    Labels
                  </FvLinkButton>
                )}
                {hasAsk && (
                  <PjPromptPopup
                    contextMessage={buildShipmentContextMessage(load.loadId)}
                    trigger={
                      <FvLinkButton icon="pallet-jack">Ask PJ</FvLinkButton>
                    }
                  />
                )}
                <FvLinkButton
                  to={routes.details(load.loadId)}
                  icon="ellipsis-h"
                >
                  More
                </FvLinkButton>
              </DividedContent>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}
